/* eslint-disable no-unused-vars */
import React from 'react'

// Elements
import BlogBinkFilter from './fields/BlogBinkFilter'
import BlogBinkButton from './fields/BlogBinkButton'
import BlogBinkPagination from './fields/BlogBinkPagination'

export { BlogBinkFilter, BlogBinkButton, BlogBinkPagination }

export const BlogContext = React.createContext({})

export const BlogProvider = BlogContext.Provider
export const BlogConsumer = BlogContext.Consumer

type BlogBinkPost = {
  node: {
    databaseId: number
    [key: string]: any
  }
}
export type BlogBinkPosts = Array<BlogBinkPost>

export interface BlogBinkProps {
  id: string
  showIds?: Array<number>
  hideIds?: Array<number>
  limit?: number
  pagination?: boolean
  categoryName?: string
  children: React.ReactNode
  posts: BlogBinkPosts
  categories?: any
}

export interface ContextProps {
  offset: number
  setOffset(value: number): void
  isSelected(value: string): boolean
  limit: number
  pagination: boolean
  showMoreButton: boolean
  showLessButton: boolean
  showPagination: boolean
  decrementOffset(): void
  incrementOffset(): void
  setSelectedFilters(value: string): void
  reset(): void
  posts: BlogBinkPosts
  unfilteredPosts: BlogBinkPosts
  categories: any
  selectedFilters: Array<string>
}

const BlogBink: React.FC<BlogBinkProps> = ({
  posts,
  categories = [],
  id = 'blog',
  showIds = [],
  hideIds = [],
  limit = 6,
  pagination = false,
  categoryName = 'categories',
  children,
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState<Array<string>>(
    []
  )
  const [offset, setOffset] = React.useState<number>(1)

  const setSelectedFiltersLocal = (filterId: string): void => {
    const newSelectedFilters = selectedFilters.map((x) => x)
    const filterIndex: number = newSelectedFilters.indexOf(filterId)

    if (filterIndex === -1) {
      newSelectedFilters.push(filterId)
    } else {
      newSelectedFilters.splice(filterIndex, 1)
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(`${id}-filters`, JSON.stringify(newSelectedFilters))
    }

    setOffset(1)
    setSelectedFilters(newSelectedFilters)
  }

  const incrementOffset = (): void => {
    setOffset((prevState: number) => Number(prevState + 1))
  }

  const decrementOffset = (): void => {
    setOffset((prevState: number) => Number(prevState - 1))
  }

  const reset = (): void => {
    setSelectedFilters([])
    setOffset(1)

    localStorage.removeItem(`${id}-filters`)
  }

  const isSelected = (filterId: string): boolean =>
    selectedFilters.indexOf(filterId) !== -1

  const filterPosts = (items: BlogBinkPosts) =>
    items.filter(({ node }, index: number) => {
      let response = true

      const measureIndex = index + 1
      const measureCurrentOffset = offset * limit
      const measurePreviousOffset = (offset - 1) * limit

      if (
        !pagination &&
        selectedFilters.length === 0 &&
        limit !== null &&
        measureIndex > measureCurrentOffset
      ) {
        return false
      }

      if (
        pagination &&
        selectedFilters.length === 0 &&
        limit !== null &&
        (measureIndex <= measurePreviousOffset || measureIndex > measureCurrentOffset)
      ) {
        return false
      }

      if (showIds.length > 0 && showIds.indexOf(node.databaseId) === -1) {
        return false
      }

      if (hideIds.length > 0 && hideIds.indexOf(node.databaseId) !== -1) {
        return false
      }

      const nodeCategories: any = node[categoryName]
        ? node[categoryName]
        : false

      if (selectedFilters.length > 0 && nodeCategories) {
        response = false

        nodeCategories.nodes.forEach((category: any) => {
          if (selectedFilters.indexOf(category.id) !== -1) {
            response = true
          }
        })
      }

      return response
    })

  React.useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      const storageSelectedFilters = localStorage.getItem(`${id}-filters`)

      if (storageSelectedFilters) {
        setSelectedFilters(JSON.parse(storageSelectedFilters))
      }
    }
  }, [])

  const edges = filterPosts(posts)

  const maxPosts =
    showIds.length > 0 || hideIds.length > 0 ? showIds.length : posts.length

  return (
    <BlogProvider
      value={{
        setSelectedFilters: setSelectedFiltersLocal,
        selectedFilters,
        isSelected,
        setOffset,
        incrementOffset,
        decrementOffset,
        offset,
        limit,
        reset,
        filterPosts,
        unfilteredPosts: posts,
        posts: edges,
        categories,
        hasPosts: edges.length > 0,
        showPagination: selectedFilters.length === 0 && pagination === true,
        pagination,
        showMoreButton:
          selectedFilters.length === 0 &&
          maxPosts > edges.length &&
          offset * limit < maxPosts,
        showLessButton: selectedFilters.length === 0 && offset > 1,
      }}
    >
      {children}
    </BlogProvider>
  )
}

export default BlogBink
